import plusIcon from "../../assets/icons/plus.svg";
import { Trash2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { showSuccess, showError } from "../../components/ui/toast";
import { getToken, getUser } from "../../services/authStore";
import "./status.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function StatusModule() {
  const token = getToken();
  const user = getUser();

  const role = user?.role || "";

  const canShowGlobalToggle =
    role === "super_admin" || role === "support_admin";
  const canShowStage = role === "super_admin" || role === "support_admin";
  const [statuses, setStatuses] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isFirstSave, setIsFirstSave] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_URL}/settings/status/${user.company_id}/${role}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = res.data || [];

      setIsFirstSave(data.length === 0);

      setStatuses(
        data.map((item) => ({
          id: item.id,
          name: item.name || "",
          color: item.colour
            ? item.colour.startsWith("#")
              ? item.colour
              : `#${item.colour.padStart(6, "0")}`
            : "#000000",
          status: item.status ?? 1,
          isglobal: item.isglobal ?? 0,
          stage: item.stage ?? null,
        })),
      );
    } catch (error) {
      console.error("Fetch error:", error);
      showError("Failed to load status");
    }
  }, [token, user.company_id, role]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleAddRow = () => {
    setStatuses((prev) => [
      ...prev,
      {
        id: null,
        name: "",
        color: "#000000",
        status: 1,
        isglobal: 0,
        stage: null,
      },
    ]);
  };

  // ================= UPDATE FIELD =================
  const updateField = (index, key, value) => {
    setStatuses((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    );
  };

  // ================= DELETE ROW =================
  const handleDeleteRow = async (index) => {
    const item = statuses[index];

    if (!item) return;

    if (!item.id) {
      setStatuses((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    if (!window.confirm("Are you sure you want to delete this status?")) return;

    try {
      await axios.delete(
        `${API_URL}/settings/status/${user.company_id}/${item.id}/${role}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setStatuses((prev) => prev.filter((_, i) => i !== index));
      showSuccess("Deleted Successfully");
    } catch (error) {
      console.error("Delete error:", error);

      const backendMsg =
        error?.response?.data?.detail || error?.response?.data?.message;

      if (backendMsg) {
        if (Array.isArray(backendMsg)) {
          showError(backendMsg.map((e) => e.msg).join(", "));
        } else {
          showError(backendMsg);
        }
      } else {
        showError("Delete Failed");
      }
    }
  };

  // ================= SAVE FUNCTION =================
  const handleSave = async () => {
    if (statuses.some((s) => !s.name.trim())) {
      showError("Name cannot be empty");
      return;
    }

    setIsSaving(true);

    try {
      const baseURL = `${API_URL}/settings/status/${user.company_id}/${role}`;

      // Prepare the payload
      const payload = statuses.map((s) => ({
        name: s.name.trim(),
        colour: s.color.replace("#", ""),
        status: s.status ?? 1,
        isglobal: canShowGlobalToggle ? (s.isglobal ?? 0) : 0,
        stage: canShowStage ? s.stage : null,
      }));

      if (isFirstSave) {
        // POST expects an array of status objects
        await axios.post(baseURL, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        setIsFirstSave(false);
        showSuccess("Statuses created successfully");
      } else {
        // PUT expects array with IDs included
        const updatePayload = statuses.map((s) => ({
          id: s.id || 0,
          name: s.name.trim(),
          colour: s.color.replace("#", ""),
          status: s.status ?? 1,
          isglobal: canShowGlobalToggle ? (s.isglobal ?? 0) : 0,
          stage: canShowStage ? s.stage : null, // ✅ ADD THIS
        }));

        await axios.put(baseURL, updatePayload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        showSuccess("Statuses updated successfully");
      }
    } catch (error) {
      console.error("Save error:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);

        // Show more detailed error message
        const errorMessage = error.response.data?.detail
          ? JSON.stringify(error.response.data.detail)
          : error.response.data?.message || "Save Failed";

        showError(`Save Failed: ${errorMessage}`);
      } else {
        showError("Save Failed");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // ================= SEARCH FILTER =================
  const filteredStatuses = statuses.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ================= UI =================
  return (
    <div className="status-wrapper">
      <div className="status-container">
        <div className="status-header">
          <span className="status-title"></span>

          <div className="status-header-right">
            <input
              type="text"
              placeholder="Search status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="status-search"
            />

            <div className="status-add-btn" onClick={handleAddRow}>
              <img src={plusIcon} alt="add" />
            </div>
          </div>
        </div>

        <div className="status-table-wrapper">
          <div className="status-scroll-wrapper">
            <table className="status-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Status Name</th>
                  <th>Color</th>
                  <th>Status</th>
                  {canShowStage && <th>Stage</th>}
                  {canShowGlobalToggle && <th>Is Global</th>}

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredStatuses.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>

                    <td>
                      <input
                        value={item.name}
                        onChange={(e) =>
                          updateField(index, "name", e.target.value)
                        }
                        placeholder="Enter status name"
                      />
                    </td>

                    <td>
                      <input
                        type="color"
                        value={item.color}
                        onChange={(e) =>
                          updateField(index, "color", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={item.status === 1}
                          onChange={(e) =>
                            updateField(
                              index,
                              "status",
                              e.target.checked ? 1 : 0,
                            )
                          }
                        />
                        <span className="slider"></span>
                      </label>
                    </td>
                    {canShowStage && (
                      <td>
                        <select
                          className="status-dropdown"
                          value={item.stage ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newStage =
                              value === "" ? null : Number(value);

                            setStatuses((prev) =>
                              prev.map((item, i) => {
                                // If current row → set new value
                                if (i === index) {
                                  return { ...item, stage: newStage };
                                }

                                // ❗ Remove duplicate Initial/Last from other rows
                                if (
                                  newStage !== null &&
                                  item.stage === newStage
                                ) {
                                  return { ...item, stage: null };
                                }

                                return item;
                              }),
                            );
                          }}
                        >
                          <option value="" disabled>
                            Select Stage
                          </option>
                          <option value={0}>Initial</option>
                          <option value={1}>Last</option>
                        </select>
                      </td>
                    )}
                    {canShowGlobalToggle && (
                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={item.isglobal === 1}
                            onChange={(e) =>
                              updateField(
                                index,
                                "isglobal",
                                e.target.checked ? 1 : 0,
                              )
                            }
                          />
                          <span className="slider"></span>
                        </label>
                      </td>
                    )}

                    <td>
                      <Trash2
                        size={18}
                        className="status-action-icon delete"
                        onClick={() => handleDeleteRow(index)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ textAlign: "right", padding: "20px" }}>
          <button className="save-btn" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
